/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2023 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2023 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { LinearProgress } from '@mui/material';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { ApiModule } from '@opengeoweb/api';
import {
  filterMapPresets,
  LayerManagerConnect,
  MultiMapDimensionSelectConnect,
  SyncGroupViewerConnect,
  SelectedFeaturesDialogConnect,
} from '@opengeoweb/core';
import { PluginDebugConnect } from '@opengeoweb/plugins';
import type { ConfigType } from '@opengeoweb/shared';
import {
  AppLayout,
  ErrorBoundary,
  sessionStorageProvider,
} from '@opengeoweb/shared';
import {
  LocationsMenuConnect,
  SoundingsManagerConnect,
  SoundingsProfileDataDialogConnect,
  SoundingsSourceDialogConnect,
} from '@opengeoweb/soundings';
import { mapActions, mapSelectors } from '@opengeoweb/store';
import {
  LocationInfoConnect,
  LocationSelectDialogConnect as TimeSeriesLocationSelectDialogConnect,
  TimeSeriesManagerConnect,
} from '@opengeoweb/time-series';
import {
  DrawingToolConnect,
  AreaDrawingManagerConnect,
  PublicWarningsFormDialogConnect,
  WarningEventDialogConnect,
  getWarningsFeatures,
} from '@opengeoweb/warnings';
import {
  DataExplorerTimeseriesConnect,
  WorkspaceDetail,
  WorkspaceTopBar,
} from '@opengeoweb/workspace';
import type { WorkspaceLookupFunctionType } from '@opengeoweb/workspace';
import type { ComponentsLookUpPayload } from './componentsLookUp';
import { componentsLookUp } from './componentsLookUp';
import Header from './Header';
import BrowserIssueDialog from '../../components/BrowserIssueDialog';
import type { AppStore } from '../../store';
import { useInitialPresets } from '../../utils/app-api/hooks';

interface WorkspaceLayoutProps extends Omit<ApiModule, 'config'> {
  workspaceId?: string;
  config?: ConfigType;
}

export const WorkspaceLayout: React.FC<WorkspaceLayoutProps> = ({
  workspaceId,
  config,
  auth,
  onSetAuth,
}: WorkspaceLayoutProps) => {
  const dispatch = useDispatch();
  const defaultMapSettings = useSelector((store: AppStore) =>
    mapSelectors.getdefaultMapSettings(store),
  );

  const hasAuthenticated =
    sessionStorageProvider.getHasAuthenticated() === 'true';

  const { data: initialPresetsResult, isLoading: isInitialPresetsLoading } =
    useInitialPresets(
      config?.GW_INITIAL_PRESETS_FILENAME ?? 'initialPresets.json',
    );

  React.useEffect(() => {
    if (isInitialPresetsLoading || !initialPresetsResult) {
      return;
    }

    if (initialPresetsResult.preset.defaultMapSettings) {
      dispatch(
        mapActions.setDefaultMapSettings({
          preset: initialPresetsResult.preset.defaultMapSettings,
        }),
      );
    }
  }, [dispatch, initialPresetsResult, isInitialPresetsLoading]);

  const onComponentsLookUp = (
    payload: ComponentsLookUpPayload,
  ): React.ReactElement => {
    return componentsLookUp(payload, config, auth, onSetAuth);
  };

  const hasPluginsConfigured =
    (config?.GW_PLUGIN_BACKEND_URL || config?.GW_PLUGIN_TEST_URL) &&
    config?.GW_PLUGIN_DEBUG === true;

  // pass an empty config instead of undefined, so the session config is not used as fallback
  const { hasPublicWarnings, hasMaritimeWarnings, hasWarningEvents } =
    getWarningsFeatures(config ?? {});
  const showPublicWarningsFormDialog =
    hasPublicWarnings ||
    hasMaritimeWarnings ||
    !!config?.GW_SIGMET_BASE_URL ||
    !!config?.GW_AIRMET_BASE_URL;

  // // wait before the initial presets have been loaded before loading the app
  if (isInitialPresetsLoading) {
    return <LinearProgress color="secondary" />;
  }

  const {
    baseLayers,
    services,
    baseServices,
    timeSeriesServices,
    soundingsCollections,
  } = filterMapPresets(initialPresetsResult ?? null);

  return (
    <AppLayout
      header={
        <Header
          config={config}
          workspaceTopBar={
            <WorkspaceTopBar
              appTitle={config?.GW_FEATURE_APP_TITLE}
              workspaceId={workspaceId}
            />
          }
        />
      }
    >
      <WorkspaceDetail
        componentsLookUp={onComponentsLookUp as WorkspaceLookupFunctionType}
        initialWorkspaceId={config?.GW_INITIAL_WORKSPACE_PRESET}
        openWorkspaceMenuOnStartup={
          !!config?.GW_OPEN_WORKSPACE_MENU_ON_STARTUP && hasAuthenticated
            ? true
            : undefined
        }
        workspaceId={workspaceId}
      >
        <BrowserIssueDialog />
        <ErrorBoundary>
          <SyncGroupViewerConnect />
          <TimeSeriesManagerConnect
            preloadedTimeseriesServices={timeSeriesServices}
          />
          <SoundingsSourceDialogConnect
            soundingsCollections={soundingsCollections}
          />
          {soundingsCollections ? (
            <LocationsMenuConnect soundingsCollections={soundingsCollections} />
          ) : null}

          <SoundingsManagerConnect />
          <SoundingsProfileDataDialogConnect />
          <TimeSeriesLocationSelectDialogConnect />
          <LocationInfoConnect />
          <LayerManagerConnect
            bounds="parent"
            preloadedAvailableBaseLayers={baseLayers}
            preloadedBaseServices={baseServices ?? services}
            preloadedMapServices={services}
            showMapIdInTitle
          />
          <SelectedFeaturesDialogConnect />
          {config?.GW_DATA_EXPLORER_CONFIGURATION_FILENAME ? (
            <DataExplorerTimeseriesConnect
              productConfigKey={config?.GW_DATA_EXPLORER_CONFIGURATION_FILENAME}
            />
          ) : null}
          <MultiMapDimensionSelectConnect />
          {config?.GW_DRAWINGS_BASE_URL !== undefined && (
            <>
              <AreaDrawingManagerConnect />
              <DrawingToolConnect showMapIdInTitle />
            </>
          )}
          {showPublicWarningsFormDialog ? (
            <PublicWarningsFormDialogConnect
              defaultMapPreset={defaultMapSettings}
              startPosition={{ top: 40, left: 60 }}
            />
          ) : null}
          {hasWarningEvents ? (
            <WarningEventDialogConnect
              defaultMapPreset={defaultMapSettings}
              startPosition={{ top: 40, left: 60 }}
            />
          ) : null}
          {!!hasPluginsConfigured && <PluginDebugConnect />}
        </ErrorBoundary>
      </WorkspaceDetail>
    </AppLayout>
  );
};
